import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ApprovedPolicyListPage = () => {
  const navigate = useNavigate();
  const [assignedCases, setAssignedCases] = useState([
    {
      id: 'case-1',
      policyNumber: 'POL-2024-004',
      type: 'Auto Insurance',
      client: 'David Wilson',
      submittedDate: '2024-01-25',
      complexity: 'moderate',
      status: 'pending_review'
    },
    {
      id: 'case-2',
      policyNumber: 'POL-2024-005',
      type: 'Home Insurance',
      client: 'Linda Brown',
      submittedDate: '2024-01-26',
      complexity: 'simple',
      status: 'pending_review'
    },
    {
      id: 'case-3',
      policyNumber: 'POL-2024-006',
      type: 'Commercial Insurance',
      client: 'XYZ Corp',
      submittedDate: '2024-01-26',
      complexity: 'complex',
      status: 'pending_review'
    }
  ]);

  // 添加 status 字段到 approved policies
  const [approvedPolicies, setApprovedPolicies] = useState([
    {
      id: 1,
      policyNumber: 'POL-2024-001',
      type: 'Auto Insurance',
      client: 'John Smith',
      approvedDate: '2024-01-20',
      complexity: 'simple',
      underwriter: 'Sarah Wilson',
      status: 'approved'  // 添加 status 字段
    },
    {
      id: 2,
      policyNumber: 'POL-2024-002',
      type: 'Home Insurance',
      client: 'Emma Johnson',
      approvedDate: '2024-01-21',
      complexity: 'moderate',
      underwriter: 'Michael Brown',
      status: 'approved'  // 添加 status 字段
    },
    {
      id: 3,
      policyNumber: 'POL-2024-003',
      type: 'Commercial Insurance',
      client: 'ABC Company',
      approvedDate: '2024-01-22',
      complexity: 'complex',
      underwriter: 'David Lee',
      status: 'approved'  // 添加 status 字段
    }
  ]);

  // 统一的状态配置
  const STATUS_CONFIG = {
    pending_review: { label: 'Pending Review', color: 'warning' },
    in_review: { label: 'In Review', color: 'info' },
    approved: { label: 'Approved', color: 'success' },
    rejected: { label: 'Rejected', color: 'error' }
  };

  // 统一的状态标签组件
  const getStatusChip = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG['pending_review'];
    return (
      <Chip 
        label={config.label}
        color={config.color}
        size="small"
        sx={{ 
          fontWeight: 500,
          '& .MuiChip-label': { px: 2 }
        }}
      />
    );
  };

  const getComplexityChip = (complexity) => {
    const config = {
      simple: { color: 'success', label: 'Simple' },
      moderate: { color: 'warning', label: 'Moderate' },
      complex: { color: 'error', label: 'Complex' }
    };

    return (
      <Chip
        size="small"
        color={config[complexity].color}
        label={config[complexity].label}
        variant="outlined"
      />
    );
  };

  return (
    <Container maxWidth="xl">
      {/* Header Section - 修改标题并删除返回按钮 */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          My Review List
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Manage and review your assigned insurance policies
        </Typography>
      </Box>

      {/* My Assigned Cases Section */}
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Box sx={{ 
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              My Assigned Cases
            </Typography>
            <Chip 
              label={`${assignedCases.length} cases`}
              sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
            />
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Policy Number</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Submitted Date</TableCell>
                <TableCell>Complexity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedCases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No assigned cases yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                assignedCases.map((caseItem) => (
                  <TableRow 
                    key={caseItem.id}
                    sx={{ 
                      '&:hover': { bgcolor: '#f8f9fa' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>{caseItem.policyNumber}</TableCell>
                    <TableCell>{caseItem.type}</TableCell>
                    <TableCell>{caseItem.client}</TableCell>
                    <TableCell>{caseItem.submittedDate}</TableCell>
                    <TableCell>{getComplexityChip(caseItem.complexity)}</TableCell>
                    <TableCell>{getStatusChip(caseItem.status)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Review">
                        <IconButton 
                          size="small"
                          onClick={() => navigate(`/underwriter/policy-review/${caseItem.id}`)}
                          sx={{ color: '#1976d2' }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Approved Policies Table */}
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Box sx={{ 
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              Approved Policies
            </Typography>
            <Chip 
              label={`${approvedPolicies.length} policies`}
              sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
            />
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Policy Number</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Approved Date</TableCell>
                <TableCell>Complexity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Underwriter</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvedPolicies.map((policy) => (
                <TableRow 
                  key={policy.id}
                  sx={{ 
                    '&:hover': { bgcolor: '#f8f9fa' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>{policy.policyNumber}</TableCell>
                  <TableCell>{policy.type}</TableCell>
                  <TableCell>{policy.client}</TableCell>
                  <TableCell>{policy.approvedDate}</TableCell>
                  <TableCell>{getComplexityChip(policy.complexity)}</TableCell>
                  <TableCell>{getStatusChip(policy.status)}</TableCell>
                  <TableCell>{policy.underwriter}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => navigate(`/underwriter/approved/${policy.id}`)}
                        sx={{ color: '#1976d2' }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default ApprovedPolicyListPage; 